import type { Session as NextAuthSession } from 'next-auth';
import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';

import { complianceApi } from '~/shared/services/api';
import { TICKET_CONSTANTS } from '~/shared/constants';
import { mountAuthHeaders } from '~/shared/auth/mountAuthHeaders';

import { ApiTicket } from '../types';

const { QUERY } = TICKET_CONSTANTS;

export const GET_TICKETS_QUERY_KEY = 'useGetTickets';

export type GetTicketsAPIResponse = ApiTicket[];

export async function getTickets(session?: NextAuthSession | null) {
  const response = await complianceApi.get<ApiTicket[]>('/ticket', {
    headers: mountAuthHeaders(session),
  });

  return response.data;
}

export function useGetTickets<T = GetTicketsAPIResponse>(
  queryOptions?: Omit<
    UseQueryOptions<GetTicketsAPIResponse, unknown, T, string[]>,
    'queryKey' | 'queryFn'
  >
) {
  const { data: session } = useSession();
  const userId = (session?.user as { id?: string })?.id;

  const queryFn = () => getTickets(session);

  return useQuery({
    queryKey: [GET_TICKETS_QUERY_KEY, userId || ''],
    queryFn,
    retry: (failureCount: number, error: any) => {
      if (error?.response?.status === 401) {
        return false;
      }
      return failureCount < QUERY.MAX_RETRIES;
    },
    ...queryOptions,
  });
}
